import React, { useState } from 'react';
import { formatMoney } from '../../../utils/currency';
import type { CurrencyCode } from '@bbpos/types';
import { Badge } from '../../BasicComponents/Badge';
import { Button } from '../../BasicComponents/Button';
import { Card } from '../../CompositeComponents/Card';
import type { ApprovalCardProps } from './types';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmt(paise: number, currency: CurrencyCode) {
  return formatMoney(paise, currency);
}

const TYPE_LABEL: Record<string, string> = {
  EXPENSE: 'Expense',
  DISCOUNT: 'Discount Override',
  STOCK_TRANSFER: 'Stock Transfer',
  RETURN: 'High-Value Return',
};

// ─── ApprovalCard ─────────────────────────────────────────────────────────────

export function ApprovalCard({
  approval,
  onApprove,
  onReject,
  onHold,
  isSelfSubmitted = false,
  duplicateWarning = null,
  loading = false,
  currency = 'INR',
  className,
}: ApprovalCardProps) {
  const [showRejectInput, setShowRejectInput] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [approving, setApproving] = useState(false);
  const [holding, setHolding] = useState(false);

  const actionsDisabled = loading || isSelfSubmitted;

  const submittedAt = approval.submittedAt.toLocaleString('en-IN', {
    dateStyle: 'medium',
    timeStyle: 'short',
  });

  async function handleApprove() {
    setApproving(true);
    await onApprove?.();
    setApproving(false);
  }

  async function handleHold() {
    setHolding(true);
    await onHold?.();
    setHolding(false);
  }

  function handleRejectConfirm() {
    if (!rejectReason.trim()) return;
    onReject?.(rejectReason.trim());
    setShowRejectInput(false);
    setRejectReason('');
  }

  return (
    <Card variant="outlined" padding="md" className={className} aria-label={`Approval request: ${TYPE_LABEL[approval.type] ?? approval.type}`}>
      {/* Duplicate warning */}
      {duplicateWarning && (
        <div role="alert" className="flex items-center gap-1.5 bg-warning-50 border border-warning-300 rounded-md px-3 py-1.5 mb-3 text-xs text-warning-800">
          <svg className="w-3.5 h-3.5 shrink-0" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path d="M8 2L14 13H2L8 2z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
            <path d="M8 7v2M8 11v.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
          {duplicateWarning}
        </div>
      )}

      {/* Header */}
      <div className="flex items-start justify-between mb-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <Badge variant="pending" size="sm">Pending</Badge>
            <span className="text-xs text-neutral-500">{TYPE_LABEL[approval.type] ?? approval.type}</span>
          </div>
          <p className="font-bold text-neutral-900 text-lg tabular-nums">{fmt(approval.amount, currency)}</p>
        </div>
      </div>

      {/* Meta */}
      <dl className="text-xs text-neutral-600 space-y-0.5 mb-3">
        <div className="flex justify-between">
          <dt>Submitted by</dt>
          <dd className="font-medium text-neutral-800">{approval.submittedBy}</dd>
        </div>
        <div className="flex justify-between">
          <dt>Date</dt>
          <dd>{submittedAt}</dd>
        </div>
        {approval.details && (
          <div className="pt-1">
            <dt className="text-neutral-500">Details</dt>
            <dd className="mt-0.5 text-neutral-700">{approval.details}</dd>
          </div>
        )}
      </dl>

      {/* Self-blocked notice */}
      {isSelfSubmitted && (
        <p className="text-xs text-warning-700 bg-warning-50 border border-warning-200 rounded-md px-3 py-1.5 mb-3">
          Cannot approve your own submission
        </p>
      )}

      {/* Reject reason input */}
      {showRejectInput && (
        <div className="mb-3 space-y-2">
          <label className="text-xs font-medium text-neutral-700 block">
            Rejection reason <span aria-hidden="true" className="text-danger-500">*</span>
          </label>
          <input
            type="text"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="Provide a reason..."
            aria-required="true"
            aria-label="Rejection reason (required)"
            className="w-full border border-neutral-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
            autoFocus
          />
          <div className="flex gap-2">
            <button
              type="button"
              disabled={!rejectReason.trim()}
              onClick={handleRejectConfirm}
              className="flex-1 py-1.5 bg-danger-600 text-white text-sm rounded-md font-medium hover:bg-danger-700 disabled:opacity-50"
            >
              Confirm Rejection
            </button>
            <button
              type="button"
              onClick={() => { setShowRejectInput(false); setRejectReason(''); }}
              className="py-1.5 px-3 border border-neutral-300 text-neutral-700 text-sm rounded-md hover:bg-neutral-50"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Action buttons */}
      {!showRejectInput && (
        <div className="flex gap-2">
          {onApprove && (
            <Button
              variant="primary"
              size="sm"
              disabled={actionsDisabled}
              loading={approving}
              onClick={handleApprove}
              aria-label="Approve this request"
              title={isSelfSubmitted ? 'Cannot approve own expense' : undefined}
              className="flex-1"
            >
              Approve
            </Button>
          )}
          {onReject && (
            <Button
              variant="danger"
              size="sm"
              disabled={actionsDisabled}
              onClick={() => setShowRejectInput(true)}
              aria-label="Reject this request"
              title={isSelfSubmitted ? 'Cannot reject own expense' : undefined}
              className="flex-1"
            >
              Reject
            </Button>
          )}
          {onHold && (
            <Button
              variant="secondary"
              size="sm"
              disabled={actionsDisabled}
              loading={holding}
              onClick={handleHold}
              aria-label="Put this request on hold"
              className="flex-1"
            >
              Hold
            </Button>
          )}
        </div>
      )}
    </Card>
  );
}
